// src/features/locations/LocationPropertyPane.jsx
//
// LEASES-OVERVIEW-REDESIGN — Read-only property pane for the Lease Overview
// 2×3 grid (row 1, left cell). Shows photo/map toggle, photo/map frame,
// address line, premises, and location ID.
//
// Does NOT include upload / Replace / Delete admin affordances — display only.
// Accepts the full location object returned by getLocation(id) from locationsClient.
// Renders a graceful empty state when location is null (fetch failed / not found).
//
// The photo/map toggle mirrors PhotoPanel (same ToggleSeg pattern) but is
// entirely self-contained so PhotoPanel and LocationDetail are unaffected.
//
// L-50: map embed URL is built client-side from the location's stored
// coordinates (geo_lat/geo_lng preferred, lat/lng fallback) — no API key
// or server round-trip required. Falls back to address-based embed when
// coordinates are absent but an address is present.

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { STAGES, T } from "../../app/tokens.js";
import { PaneHeading } from "../../components/PaneHeading.jsx";
import { buildLocationUrl } from "../../services/buildUrl.js";
import { OFFICE_LEASE_LINK_TYPE_STYLE } from "./officeLeaseLinkStyle.js";
import { LeaseAreasStrip } from "./LeaseAreasStrip.jsx";

// ─── Photo / map frame style ──────────────────────────────────────────────────

const FRAME = {
  position: "relative",
  width: "100%",
  flex: "1 1 0",
  minHeight: 120,
  borderRadius: 8,
  overflow: "hidden",
  background: T.bg,
  border: `1px solid ${T.border}`,
};

// ─── Coordinate coercion (mirrors PhotoPanel.toCoord) ────────────────────────

function toCoord(v) {
  if (v == null) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

// ─── Segmented toggle button ──────────────────────────────────────────────────

function ToggleSeg({ label, active, onClick, disabled, testid, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      aria-disabled={disabled || undefined}
      data-testid={testid}
      title={title}
      style={{
        padding: "4px 12px",
        fontSize: T.fsXs5,
        fontWeight: active ? 600 : 500,
        color: disabled ? T.light : (active ? T.dark : T.mid),
        background: active ? T.white : "transparent",
        border: "none",
        borderRadius: 5,
        boxShadow: active ? T.cardShadow : "none",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        transition: "background 0.12s, color 0.12s, box-shadow 0.12s",
      }}
    >
      {label}
    </button>
  );
}

// ─── LocationPropertyPane ─────────────────────────────────────────────────────

/**
 * @param {object|null} props.location  Full location row from getLocation().
 *   Uses: id, imageUrl, market, address, suite, premises,
 *         geo_lat, geo_lng, lat, lng.
 */
export function LocationPropertyPane({ location, lease, siblingLeases = [], onEditAreas, asOf }) {
  const [view, setView] = useState("photo");
  const navigate = useNavigate();

  // Reset view when the location changes.
  // Only force back to "photo" when the new location has no map capability
  // (no coordinates and no address). When the new location supports a map
  // and the user had Map selected, keep it on Map.
  useEffect(() => {
    if (!location) return;
    const geoLat = toCoord(location.geo_lat ?? location.geoLat);
    const lat    = geoLat != null ? geoLat : toCoord(location.lat);
    const geoLng = toCoord(location.geo_lng ?? location.geoLng);
    const lng    = geoLng != null ? geoLng : toCoord(location.lng);
    const hasCoords  = lat != null && lng != null;
    const hasAddress = Boolean(location.address?.trim());
    const mapEnabled = hasCoords || hasAddress;
    if (!mapEnabled) {
      setView("photo");
    }
  }, [location]);

  // ── Derived flags ─────────────────────────────────────────────────────────
  const hasAddress = Boolean(location?.address?.trim());
  const hasPhoto   = Boolean(location?.imageUrl);
  const address    = location?.address || null;
  const locationId = location?.id || lease?.locationId || null;
  const officeCity = location?.market || lease?.city || "";
  const officeName = location?.locationAlias || lease?.locationAlias || "";
  const officeStage = location?.stage || lease?.locationStage || "";
  const officeStageLabel = STAGES[officeStage]?.label || officeStage || "Stage unavailable";
  const officeIdentity = [officeCity, officeName].filter(Boolean).join(" · ");

  // Prefer geo_lat/geo_lng (geocoded); fall back to legacy lat/lng.
  const lat = (() => {
    const geoLat = toCoord(location?.geo_lat ?? location?.geoLat);
    return geoLat != null ? geoLat : toCoord(location?.lat);
  })();
  const lng = (() => {
    const geoLng = toCoord(location?.geo_lng ?? location?.geoLng);
    return geoLng != null ? geoLng : toCoord(location?.lng);
  })();

  const hasCoords = lat != null && lng != null;

  // Build embed URL client-side — no API key, no server round-trip.
  const mapUrl = hasCoords
    ? `https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`
    : hasAddress
    ? `https://maps.google.com/maps?q=${encodeURIComponent(location.address.trim())}&z=15&output=embed`
    : null;

  const mapEnabled = hasCoords || hasAddress;

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <section
      data-testid="location-property-pane"
      style={{
        background:    T.card,
        border:        `1px solid ${T.border}`,
        borderRadius:  6,
        height:        "100%",
        minHeight:     420,
        boxSizing:     "border-box",
        display:       "flex",
        flexDirection: "column",
      }}
    >
      {/* Header: "Property" heading + Photo/Map toggle */}
      <div
        style={{
          padding:    "8px 14px",
          display:    "flex",
          alignItems: "center",
          gap:        9,
          minHeight:  40,
          flexShrink: 0,
        }}
      >
        <PaneHeading style={{ marginBottom: 0, flex: 1 }}>Property</PaneHeading>
        <div
          role="group"
          aria-label="Photo or map view"
          data-testid="property-pane-photo-map-toggle"
          style={{
            display:      "inline-flex",
            gap:          2,
            padding:      2,
            borderRadius: 7,
            background:   T.lightGray,
          }}
        >
          <ToggleSeg
            label="Photo"
            active={view === "photo"}
            onClick={() => setView("photo")}
            testid="property-pane-toggle-photo"
          />
          <ToggleSeg
            label="Map"
            active={view === "map"}
            onClick={() => mapEnabled && setView("map")}
            disabled={!mapEnabled}
            testid="property-pane-toggle-map"
            title={!mapEnabled ? "No coordinates or address on file" : undefined}
          />
        </div>
      </div>

      {/* Photo / map frame */}
      <div
        style={{
          flex:          "1 1 0",
          display:       "flex",
          flexDirection: "column",
          padding:       "0 14px 8px",
        }}
      >
        <div style={FRAME} data-testid="property-pane-photo-frame">
          {view === "map" && location ? (
            mapUrl ? (
              <div
                data-testid="property-pane-map"
                style={{ position: "absolute", inset: 0 }}
              >
                <iframe
                  data-testid="property-pane-map-iframe"
                  src={mapUrl}
                  title={`Map of ${location.market || location.id}`}
                  width="100%"
                  height="100%"
                  style={{ border: 0, position: "absolute", inset: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            ) : (
              <div
                data-testid="property-pane-map-unavailable"
                style={{
                  position:        "absolute",
                  inset:           0,
                  display:         "flex",
                  alignItems:      "center",
                  justifyContent:  "center",
                  color:           T.light,
                  fontSize:        T.fsBase,
                  fontStyle:       "italic",
                }}
              >
                Map unavailable
              </div>
            )
          ) : hasPhoto ? (
            <img
              src={location.imageUrl}
              alt={`${location.market || location.id} photo`}
              style={{
                width:      "100%",
                height:     "100%",
                objectFit:  "cover",
                display:    "block",
              }}
            />
          ) : location ? (
            <div
              style={{
                display:         "flex",
                alignItems:      "center",
                justifyContent:  "center",
                width:           "100%",
                height:          "100%",
                color:           T.light,
                fontSize:        T.fsBase,
                fontWeight:      T.fwSemibold,
                background:      `repeating-linear-gradient(45deg, ${T.bg} 0 8px, ${T.card} 8px 16px)`,
              }}
            >
              No photo on file
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
                color: T.light,
                fontSize: T.fsBase,
                fontStyle: "italic",
                background: T.bg,
              }}
            >
              No property data
            </div>
          )}
        </div>
      </div>

      {/* Address */}
      <div style={{ padding: "2px 14px 0", flexShrink: 0 }}>
        {address && (
          <div
            data-testid="property-pane-address"
            style={{
              fontSize:     T.fsBase,
              color:        T.mid,
              whiteSpace:   "nowrap",
              overflow:     "hidden",
              textOverflow: "ellipsis",
              marginBottom: 3,
            }}
          >
            {address}
          </div>
        )}
      </div>
      {locationId && (
        <div
          data-testid="lease-office-tieback"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
            margin: "9px 14px 0",
            padding: "8px 10px",
            background: T.tint,
            border: `1px solid ${T.border}`,
            borderRadius: 8,
            flexShrink: 0,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 23,
              height: 23,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 6,
              background: T.blueSoft,
              color: T.blue,
              fontSize: T.fsXs,
              flexShrink: 0,
            }}
          >
            ◆
          </span>
          <span style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
            <span
              data-testid="lease-office-identity"
              style={{
                color: T.dark,
                fontSize: T.fsXs,
                fontWeight: T.fwBold,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {officeIdentity || locationId}
            </span>
            <span style={{ color: T.mid, fontSize: T.fsXxs }}>
              {locationId} · {officeStageLabel}
            </span>
          </span>
          <button
            type="button"
            data-testid="lease-open-office-link"
            onClick={() => navigate(buildLocationUrl(locationId))}
            style={{
              marginLeft: "auto",
              font: "inherit",
              ...OFFICE_LEASE_LINK_TYPE_STYLE,
              textDecoration: "none",
              whiteSpace: "nowrap",
              flexShrink: 0,
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
            }}
          >
            Open office →
          </button>
        </div>
      )}
      <LeaseAreasStrip lease={lease} location={location} siblingLeases={siblingLeases} onEditAreas={onEditAreas} asOf={asOf} />
    </section>
  );
}
